// Example: CustomerDetailPage.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Home as HomeIcon,
  Notes as NotesIcon
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCustomerById, deleteCustomer } from '../services/customerService';
import { useTranslation } from "react-i18next";

// Helper for tab panels
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navigationData = location.state?.customerData;
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(!navigationData);

  // Tab state for all your tabs
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (navigationData) {
      setCustomer(navigationData);
      setLoading(false);
    } else {
      loadCustomerFromAPI();
    }
    // eslint-disable-next-line
  }, [id, navigationData]);

  const loadCustomerFromAPI = async () => {
    try {
      setLoading(true);
      const response = await getCustomerById(id);
      const customerData = response?.data?.payload || response?.payload || response?.data || response;
      if (customerData) {
        setCustomer(customerData);
      } else {
        throw new Error('Customer not found');
      }
    } catch (error) {
      toast.error(t("customer_not_found"));
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/customers/edit/${id}`, {
      state: { customerData: customer }
    });
  };

  const handleDelete = async () => {
    const customerName = customer?.CustomerName || customer?.Name || t("customer");
    if (window.confirm(t("confirm_delete_customer", { customer: customerName }))) {
      try {
        await deleteCustomer(id);
        toast.success(t("customer_deleted_success"));
        navigate('/customers');
      } catch (error) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(t("failed_to_delete_customer"));
        }
      }
    }
  };

  const handleCreateInvoice = () => {
    navigate('/invoices/add', {
      state: {
        selectedCustomer: {
          id: customer.Id,
          name: customer.CustomerName || customer.Name
        }
      }
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Typography>{t("loading")}</Typography>
        </Box>
      </Container>
    );
  }

  if (!customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">{t("customer_not_found")}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/customers')} sx={{ color: 'primary.main' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {customer.CustomerName || customer.Name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(customer.Email || customer.email || t("no_email")) + " • " + t("customer_details")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ borderRadius: 2 }}
          >
            {t("edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ borderRadius: 2 }}
          >
            {t("delete")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateInvoice}
            sx={{ borderRadius: 2 }}
          >
            {t("create_invoice")}
          </Button>
        </Box>
      </Box>
      
      {/* Tabs and Tab Panels */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabIndex}
            onChange={(e, v) => setTabIndex(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 3 }}
          >
            <Tab label={t("customer_info_tab")} />
            <Tab label={t("proposals_tab")} />
            <Tab label={t("invoices_tab")} />
            <Tab label={t("payments_tab")} />
            <Tab label={t("credit_notes_tab")} />
            <Tab label={t("documents_tab")} />
            <Tab label={t("other_tab")} />
          </Tabs>
        </Box>
        <Divider sx={{ mb: 1 }} />

        {/* Customer Info Tab */}
        <TabPanel value={tabIndex} index={0}>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("basic_information")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("customer_name")}
                    secondary={customer.CustomerName || customer.Name || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("email")}
                    secondary={customer.Email || customer.email || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("phone")}
                    secondary={customer.PhoneNumber || customer.Phone || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("customer_type")}
                    secondary={customer.CustomerType || 'N/A'}
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("company_address")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("company_name")}
                    secondary={customer.CompanyName || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <BusinessIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("tax_number")}
                    secondary={customer.TaxNumber || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <HomeIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("address")}
                    secondary={customer.Address || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("website")}
                    secondary={customer.Website || 'N/A'}
                  />
                </ListItem>
              </List>
            </Grid>

            {/* Notes Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {t("notes")}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <NotesIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={customer.Notes || t("no_notes")}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Other tabs remain as they are, just keep your existing content */}
        <TabPanel value={tabIndex} index={1}>
          {/* Proposals content */}
        </TabPanel>
        <TabPanel value={tabIndex} index={2}>
          {/* Invoices content */}
        </TabPanel>
        <TabPanel value={tabIndex} index={3}>
          {/* Payments content */}
        </TabPanel>
        <TabPanel value={tabIndex} index={4}>
          {/* Credit Notes content */}
        </TabPanel>
        <TabPanel value={tabIndex} index={5}>
          {/* Documents content */}
        </TabPanel>
        <TabPanel value={tabIndex} index={6}>
          {/* Other content */}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default CustomerDetailPage;